
import * as React from "react"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import { Check } from "lucide-react"

import { cn } from "@/lib/utils"

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      "peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground",
      className
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      className={cn("flex items-center justify-center text-current")}
    >
      <Check className="h-4 w-4" />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
))
Checkbox.displayName = CheckboxPrimitive.Root.displayName

// Create a CheckboxGroup component to render multiple checkboxes
interface CheckboxGroupProps {
  items: { id: string; label: string; value?: string }[];
  value: string[];
  onChange: (value: string[]) => void;
  className?: string;
}

const CheckboxGroup = ({
  items,
  value,
  onChange,
  className,
}: CheckboxGroupProps) => {
  const handleCheckboxChange = (itemId: string, checked: boolean) => {
    if (checked) {
      onChange([...value, itemId]);
    } else {
      onChange(value.filter((id) => id !== itemId));
    }
  };

  return (
    <div className={cn("flex flex-col space-y-2", className)}>
      {items.map((item) => (
        <div key={item.id} className="flex items-center space-x-2">
          <Checkbox
            id={item.id}
            checked={value.includes(item.value || item.id)}
            onCheckedChange={(checked) => 
              handleCheckboxChange(item.value || item.id, checked as boolean)
            }
          />
          <label
            htmlFor={item.id}
            className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {item.label}
          </label>
        </div>
      ))}
    </div>
  );
};

export { Checkbox, CheckboxGroup };
